import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Collapse, type CollapseProps, type TableProps } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { COLUMNS } from './constants';
import { SectionLabel } from './SectionLabel';
import { LoadingTable } from '../../../shared/Loading';
import styles from './style.module.css';
import { StatisticSectionRow } from './types';
import {
  buildNestedRows,
  getActiveSectionMode,
  getSectionKey,
  getSectionRows,
  type SectionModes,
} from './sectionRows';
import tableDefaults from '../../../shared/antTableDefaults.module.css';

interface Props {
  dataSource: StatisticSectionRow[];
  loading?: boolean;
  className?: string;
  displayName?: string;
  sectionModes?: SectionModes;
  onSectionModeChange?: (sectionKey: string, mode: string) => void;
}

const MAX_INDENT_LEVEL = 2;

const getLevelClassName = (level: StatisticSectionRow['level']) => {
  const normalizedLevel = Math.min(Math.max(level ?? 0, 0), MAX_INDENT_LEVEL);

  return styles[`level-${normalizedLevel}`];
};

const getDefaultActiveKeys = (dataSource: StatisticSectionRow[]) =>
  dataSource.map(getSectionKey);

const areKeysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

const normalizeActiveKeys = (keys: string | string[]): string[] =>
  Array.isArray(keys) ? keys : [keys];

const hasNestedRows = (rows: readonly StatisticSectionRow[]) =>
  rows.some((row) => Boolean(row.children?.length));

const renderTreeExpandIcon: NonNullable<
  NonNullable<TableProps<StatisticSectionRow>['expandable']>['expandIcon']
> = ({ expanded, expandable, onExpand, record }) => {
  if (!expandable) {
    return <span className={styles.treeExpandIconPlaceholder} />;
  }

  return (
    <button
      type="button"
      aria-label={expanded ? 'Свернуть строку' : 'Развернуть строку'}
      className={styles.treeExpandIcon}
      onClick={(event) => onExpand(record, event)}
    >
      <RightOutlined
        className={cn(styles.treeExpandIconGlyph, {
          [styles.treeExpandIconGlyphExpanded]: expanded,
        })}
      />
    </button>
  );
};

export const getStatisticRowClassName = (record: StatisticSectionRow) =>
  cn(styles.statisticRow, getLevelClassName(record.level), {
    [styles.sectionRow]: Boolean(
      record.children?.length || record.isEmphasized,
    ),
    [styles.emphasizedRow]: Boolean(record.isEmphasized),
    [styles.mutedRow]: Boolean(record.isMuted),
  });

const createCollapseItems = (
  dataSource: StatisticSectionRow[],
  loading: boolean,
  activeSectionModes: SectionModes,
  onModeChange: (sectionKey: string, mode: string) => void,
): CollapseProps['items'] =>
  dataSource.map((section) => {
    const sectionKey = getSectionKey(section);
    const tableDataSource = buildNestedRows(
      getSectionRows(section, activeSectionModes),
    );
    const expandable = hasNestedRows(tableDataSource)
      ? { defaultExpandAllRows: true, expandIcon: renderTreeExpandIcon }
      : undefined;
    const activeMode = getActiveSectionMode(section, activeSectionModes);

    return {
      key: sectionKey,
      label: (
        <SectionLabel
          section={section}
          sectionKey={sectionKey}
          activeMode={activeMode}
          onModeChange={onModeChange}
        />
      ),
      children: (
        <LoadingTable<StatisticSectionRow>
          key={`${sectionKey}-${activeMode ?? 'default'}`}
          className={cn(styles.table, tableDefaults.defaultTable)}
          dataSource={tableDataSource}
          columns={COLUMNS}
          loading={loading}
          pagination={false}
          expandable={expandable}
          rowClassName={getStatisticRowClassName}
          rowKey={(record) => record.key ?? record.parameter}
        />
      ),
    };
  });

export const StatisticsSectionsTable: FC<Props> = memo(
  ({
    dataSource,
    loading = false,
    className,
    displayName,
    sectionModes,
    onSectionModeChange,
  }: Props) => {
    const sectionKeys = useMemo(
      () => getDefaultActiveKeys(dataSource),
      [dataSource],
    );
    const knownSectionKeysRef = useRef(new Set(sectionKeys));
    const [activeSectionKeys, setActiveSectionKeys] = useState(sectionKeys);
    const [internalSectionModes, setInternalSectionModes] = useState<
      Record<string, string>
    >({});
    const activeSectionModes = sectionModes ?? internalSectionModes;

    useEffect(() => {
      const knownSectionKeys = knownSectionKeysRef.current;
      const addedSectionKeys = sectionKeys.filter(
        (key) => !knownSectionKeys.has(key),
      );
      const activeSectionKeySet = new Set(activeSectionKeys);
      const nextActiveSectionKeys = sectionKeys.filter(
        (key) => activeSectionKeySet.has(key) || addedSectionKeys.includes(key),
      );

      knownSectionKeysRef.current = new Set(sectionKeys);

      if (!areKeysEqual(activeSectionKeys, nextActiveSectionKeys)) {
        setActiveSectionKeys(nextActiveSectionKeys);
      }
    }, [activeSectionKeys, sectionKeys]);

    const handleCollapseChange = useCallback<
      NonNullable<CollapseProps['onChange']>
    >((keys) => {
      setActiveSectionKeys(normalizeActiveKeys(keys));
    }, []);

    const handleSectionModeChange = useCallback(
      (sectionKey: string, mode: string) => {
        if (sectionModes === undefined) {
          setInternalSectionModes((prev) => ({ ...prev, [sectionKey]: mode }));
        }
        onSectionModeChange?.(sectionKey, mode);
      },
      [sectionModes, onSectionModeChange],
    );

    const collapseItems = useMemo(
      () =>
        createCollapseItems(
          dataSource,
          loading,
          activeSectionModes,
          handleSectionModeChange,
        ),
      [activeSectionModes, dataSource, handleSectionModeChange, loading],
    );

    return (
      <div
        className={cn(
          styles.StatisticsSectionsTable,
          tableDefaults.defaultTableContainer,
          className,
        )}
        data-display-name={displayName}
      >
        <Collapse
          bordered={false}
          className={styles.collapse}
          activeKey={activeSectionKeys}
          onChange={handleCollapseChange}
          expandIconPosition="end"
          items={collapseItems}
        />
      </div>
    );
  },
);

StatisticsSectionsTable.displayName = 'StatisticsSectionsTable';
